// src/ConsumerApp/DesignSystem/Feedback/ConfirmationModal.js

import React, { useEffect } from 'react';

const INTENT_THEMES = {
  danger: {
    iconBg: '#FEF2F2',
    iconColor: '#E9001E',
    btnBg: '#E9001E',
    btnHoverBg: '#DC2626',
    btnColor: '#FFFFFF',
    shadow: '0 3px 10px rgba(233, 0, 30, 0.28)',
  },
  warning: {
    iconBg: '#FFFBEB',
    iconColor: '#D97706',
    btnBg: '#D97706',
    btnHoverBg: '#B45309',
    btnColor: '#FFFFFF',
    shadow: '0 3px 10px rgba(217, 119, 6, 0.25)',
  },
  info: {
    iconBg: '#EFF6FF',
    iconColor: '#2563EB',
    btnBg: '#2563EB',
    btnHoverBg: '#1D4ED8',
    btnColor: '#FFFFFF',
    shadow: '0 3px 10px rgba(37, 99, 235, 0.25)',
  },
  success: {
    iconBg: '#F0FDF4',
    iconColor: '#16A34A',
    btnBg: '#16A34A',
    btnHoverBg: '#15803D',
    btnColor: '#FFFFFF',
    shadow: '0 3px 10px rgba(22, 163, 74, 0.25)',
  },
};

export default function ConfirmationModal({
  isOpen = false,
  onClose,
  onConfirm,
  isLoading = false,
  intent = 'danger', // 'danger' | 'warning' | 'info' | 'success'
  icon,
  title = 'Are you sure?',
  subtitle = 'This action cannot be undone.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) {
  // Lock background scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentTheme = INTENT_THEMES[intent] || INTENT_THEMES.danger;

  const handleBackdropClick = () => {
    if (!isLoading && onClose) {
      onClose();
    }
  };

  return (
    <>
      <style>{`
        .bb-confirm-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(3px);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          box-sizing: border-box;
          animation: bbConfirmFadeIn 0.15s ease-out;
        }

        @keyframes bbConfirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .bb-confirm-card {
          background-color: #FFFFFF;
          border-radius: 18px;
          padding: 24px;
          width: 100%;
          max-width: 360px;
          box-shadow: 0 20px 45px -10px rgba(15, 23, 42, 0.25);
          text-align: center;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: bbConfirmScaleUp 0.18s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes bbConfirmScaleUp {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .bb-confirm-icon-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          flex-shrink: 0;
        }

        .bb-confirm-title {
          margin: 0 0 6px 0;
          font-size: 17px;
          font-weight: 800;
          color: #0F172A;
          letter-spacing: -0.2px;
        }

        .bb-confirm-subtitle {
          margin: 0 0 22px 0;
          font-size: 13px;
          color: #64748B;
          line-height: 18px;
          max-width: 290px;
        }

        .bb-confirm-actions {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .bb-confirm-cancel-btn {
          flex: 1;
          padding: 11px 16px;
          border-radius: 10px;
          border: 1.5px solid #CBD5E1;
          background-color: #FFFFFF;
          color: #334155;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.15s ease, border-color 0.15s ease;
          box-sizing: border-box;
        }

        .bb-confirm-cancel-btn:hover:not(:disabled) {
          background-color: #F8FAFC;
          border-color: #94A3B8;
        }

        .bb-confirm-action-btn {
          flex: 1;
          padding: 11px 16px;
          border-radius: 10px;
          border: none;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          transition: opacity 0.15s ease, transform 0.1s ease;
          box-sizing: border-box;
        }

        .bb-confirm-action-btn:active:not(:disabled) {
          transform: scale(0.98);
        }

        /* Mobile Slide-Up Sheet (<= 640px) */
        @media (max-width: 640px) {
          .bb-confirm-overlay {
            padding: 0 !important;
            align-items: flex-end !important;
          }

          .bb-confirm-card {
            max-width: 100% !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border-top-left-radius: 22px !important;
            border-top-right-radius: 22px !important;
            padding: 24px 20px !important;
            padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
            animation: bbConfirmSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }

          @keyframes bbConfirmSlideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .bb-confirm-actions {
            flex-direction: column-reverse !important;
            gap: 10px !important;
          }

          .bb-confirm-cancel-btn,
          .bb-confirm-action-btn {
            width: 100% !important;
            height: 44px !important;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>

      <div className="bb-confirm-overlay" onClick={handleBackdropClick}>
        <div className="bb-confirm-card" onClick={(e) => e.stopPropagation()}>
          {icon && (
            <div
              className="bb-confirm-icon-circle"
              style={{ backgroundColor: currentTheme.iconBg }}
            >
              {icon}
            </div>
          )}

          <h4 className="bb-confirm-title">{title}</h4>
          <p className="bb-confirm-subtitle">{subtitle}</p>

          <div className="bb-confirm-actions">
            <button
              type="button"
              className="bb-confirm-cancel-btn"
              disabled={isLoading}
              onClick={onClose}
            >
              {cancelText}
            </button>
            <button
              type="button"
              className="bb-confirm-action-btn"
              disabled={isLoading}
              onClick={onConfirm}
              style={{
                backgroundColor: currentTheme.btnBg,
                color: currentTheme.btnColor,
                boxShadow: currentTheme.shadow,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}